import { useEffect, useMemo, useState } from "react";
import { useToast } from "../../../Hooks/ToasContext";
import { IProductEntity } from "../../../Services/api/products/products.interface";
import { CreateCollaboratorsUseCase } from "../../../Usecase/Collaborators/create-collaborators-usecase";
import { SubmitHandler, useForm } from "react-hook-form";
import { ICollaboratorEntity, ICreateCollaboratorPayload, IUpdateCollaboratorPayload } from "../../../Services/api/collaborators/collaborators.interface";
import { FindKeyWithPrefix } from "../../../utils/findKeyWithPrefix";
import { ICreateCustomerPayload, IUpdateCustomerPayload } from "../../../Services/api/customers/customers.interface";
import { useTheme } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup"
import * as Yup from 'yup'
import { ShowCollaboratorsUseCase } from "../../../Usecase/Collaborators/show-collaborators-usecase";
import { UpdateCollaboratorsUseCase } from "../../../Usecase/Collaborators/update-collaborators-usecase";

interface ICollaboratorProps {
  name: string
  username: string
  permissions: string[]
  [key: `swtich-${string}`]: boolean
}
const CreateCollaboratorSchemaValidation = Yup.object().shape({
  name: Yup.string().required('Nome é obrigatório'),
  username: Yup.string().required('E-mail é obrigatório').email('E-mail inválido')
});

interface IuseCreateCollaboratorsProps {
  createCollaboratorsUseCase: CreateCollaboratorsUseCase
  showCollaboratorsUseCase: ShowCollaboratorsUseCase
  updateCollaboratorsUseCase: UpdateCollaboratorsUseCase
  id?: string
  onlyHCP?: boolean
}
export function useCreateCollaborators({ 
  createCollaboratorsUseCase, 
  showCollaboratorsUseCase, 
  updateCollaboratorsUseCase,
  id: propId,
  onlyHCP = false
}: IuseCreateCollaboratorsProps) {
  const [loading, setLoading] = useState(false)

  const [data, setData] = useState<ICollaboratorEntity>()

  const theme = useTheme()
  const navigate = useNavigate()

  const { handleSubmit, register, control, formState, watch, setValue, reset } = useForm<ICollaboratorProps>({
    resolver: yupResolver(CreateCollaboratorSchemaValidation),
  });

  const params = useParams<{ id: string }>()
  const id = propId || params.id;

  const { errors } = formState

  const { showToast } = useToast()

  const handleAction: SubmitHandler<ICollaboratorProps> = async (values) => {
    const permissions = FindKeyWithPrefix(values, 'swtich-')

    // 1. Pega as novas permissões do formulário atual
    const formPermissions = permissions
      .filter((permission: { key: string; value: boolean }) => permission.value === true)
      .map((permission: { key: string; value: boolean }) => {
        const key = permission.key.replace('swtich-', '');
        return `${key}:manage`;
      });

    let finalPermissionsScreens = [...formPermissions];
    const hcpPrefixes = ['menu:hcp', 'feature:hcp-'];

    // 2. Se tivermos dados salvos, fazemos a mesclagem inteligente para não apagar o outro portal
    if (data?.permissionsScreens) {
      if (onlyHCP) {
        // MODO HCP: Preserva tudo o que NÃO é HCP (incluindo all e all:hub)
        const HUBPermissions = data.permissionsScreens.filter(p => 
          !hcpPrefixes.some(prefix => p.startsWith(prefix)) && 
          p !== 'all:hcp'
        );
        finalPermissionsScreens = [...HUBPermissions, ...formPermissions];
      } else {
        // MODO HUB: Preserva tudo o que É HCP (incluindo all:hcp)
        const HCPPermissions = data.permissionsScreens.filter(p => 
          hcpPrefixes.some(prefix => p.startsWith(prefix)) || p === 'all:hcp'
        );
        // Removemos o all:hub e all da base antiga para sobrescrever com o formulário atual
        const HUBBase = finalPermissionsScreens.filter(p => p !== 'all' && p !== 'all:hub');
        finalPermissionsScreens = [...HUBBase, ...HCPPermissions];
      }
    }

    // 3. Garantia de acesso à Porta da Frente do HCP
    if (finalPermissionsScreens.some((p: string) => p.startsWith('feature:hcp-')) && !finalPermissionsScreens.includes('menu:hcp:manage')) {
      finalPermissionsScreens.push('menu:hcp:manage');
    }

    // 4. Lógica de Super Admin (isolada por portal)
    // Detecta pelo formulário quais admins foram marcados (sufixo :manage = valor vindo do form)
    const isGlobalAdmin = finalPermissionsScreens.includes('all:manage');
    const isHUBAdmin = finalPermissionsScreens.includes('all:hub:manage');
    const isHCPAdmin = finalPermissionsScreens.includes('all:hcp:manage');
    if (onlyHCP) {
      // Modo HCP: só o all:hcp é gerenciado pelo formulário.
      // all e all:hub pertencem ao HUB — devem ser preservados do banco sem alteração.
      if (data?.permissionsScreens?.includes('all') && !finalPermissionsScreens.includes('all')) {
        finalPermissionsScreens.push('all');
      }
      if (data?.permissionsScreens?.includes('all:hub') && !finalPermissionsScreens.includes('all:hub')) {
        finalPermissionsScreens.push('all:hub');
      }
      if (isHCPAdmin) {
        if (!finalPermissionsScreens.includes('all:hcp')) finalPermissionsScreens.push('all:hcp');
      } else {
        finalPermissionsScreens = finalPermissionsScreens.filter(p => p !== 'all:hcp');
      }
    } else {
      // Modo HUB: all e all:hub são gerenciados pelo formulário.
      // all:hcp pertence ao HCP — já foi preservado pelo merge acima (HCPPermissions), não mexer.
      if (isGlobalAdmin) {
        if (!finalPermissionsScreens.includes('all')) finalPermissionsScreens.push('all');
      } else {
        finalPermissionsScreens = finalPermissionsScreens.filter(p => p !== 'all');
      }
      if (isHUBAdmin) {
        if (!finalPermissionsScreens.includes('all:hub')) finalPermissionsScreens.push('all:hub');
      } else {
        finalPermissionsScreens = finalPermissionsScreens.filter(p => p !== 'all:hub');
      }
      // all:hcp: preservado pelo merge, não remover aqui
    }

    // Remove valores intermediários de admin gerados com sufixo :manage (not real permission keys)
    finalPermissionsScreens = finalPermissionsScreens.filter(p =>
      p !== 'all:manage' && p !== 'all:hub:manage' && p !== 'all:hcp:manage'
    );

    // Remove entradas no formato antigo (ex: 'boxes:manage') quando já existe a versão nova ('menu:boxes:manage')
    // Isso limpa dados legados do banco sem afetar permissões válidas como 'stocks:adjust-manually:manage'
    finalPermissionsScreens = finalPermissionsScreens.filter(p => {
      if (p.startsWith('menu:') || p.startsWith('feature:') ||
          p === 'all' || p === 'all:hub' || p === 'all:hcp') {
        return true;
      }
      if (p.endsWith(':manage')) {
        return !finalPermissionsScreens.includes('menu:' + p);
      }
      return true;
    });

    // Remove duplicatas
    finalPermissionsScreens = Array.from(new Set(finalPermissionsScreens));

    try {
      setLoading(true)

      const payloadCreate: ICreateCollaboratorPayload = {
        name: values.name,
        username: values.username,
        // O backend usa permissions: ['all'] para validar acesso à API do painel admin.
        // Todos os colaboradores do sistema precisam de ['all'] para acessar os endpoints.
        // O controle granular de acesso às telas é feito exclusivamente via permissionsScreens.
        permissions: ['all'],
        permissionsScreens: finalPermissionsScreens,
      }


      const payloadUpdate: IUpdateCollaboratorPayload = {
        ...payloadCreate,
        id: String(id)
      }

      const storeResult = id ? await updateCollaboratorsUseCase.handle(payloadUpdate) : await createCollaboratorsUseCase.handle(payloadCreate)

      if (storeResult.isFailure) {
        showToast(String(storeResult?.error?.error), 'error');
        return;
      }

      showToast(id ? 'Colaborador Atualizado' : 'Colaborador Cadastrado', 'success')
      
      if (!onlyHCP) {
        navigate('/collaborators-list');
      }

    } catch (error) {
      console.log("error: ", error)
      showToast(error.response?.data?.error ?? 'Erro. Por favor, tente novamente', 'error');
    } finally {
      setLoading(false);
    }
  };



  const menuOptions = useMemo(() => {
    const allOptions = [
      {
        label: 'Administrador Global (Acesso Total ao Sistema)',
        value: 'all',
        type: 'menu',
        features: []
      },
      {
        label: 'Administrador do HUB (Acesso Total ao HUB)',
        value: 'all:hub',
        type: 'menu',
        features: []
      },
      {
        label: 'Administrador da Produção (Acesso Total à Produção)',
        value: 'all:hcp',
        type: 'menu',
        features: []
      },
      {
        label: 'Caixas',
        value: 'menu:boxes',
        type: 'menu',
        features: []
      },
      {
        label: 'Cubagens',
        value: 'menu:cubages',
        type: 'menu',
        features: []
      },
      {
        label: 'Categorias',
        value: 'menu:categories',
        type: 'menu',
        features: []
      },
      {
        label: 'Colaboradores',
        value: 'menu:collaborators',
        type: 'menu',
        features: []
      },
      {
        label: 'Clientes',
        value: 'menu:customers',
        type: 'menu',
        features: []
      },
      {
        label: 'Financeiro',
        value: 'menu:financial',
        type: 'menu',
        features: []
      },
      {
        label: 'Pedidos',
        value: 'menu:orders',
        type: 'menu',
        features: [
          {
            label: 'Cancelar Pedido',
            value: 'feature:orders-cancel'
          },
          {
            label: 'Reprocessar Pedido',
            value: 'feature:orders-reprocess'
          },
          {
            label: 'Lista de Separação',
            value: 'feature:orders-list-separation'
          }
        ]
      },
      {
        label: 'Produtos',
        value: 'menu:products',
        type: 'menu',
        features: []
      },
      {
        label: 'Estoque',
        value: 'menu:stocks',
        type: 'menu',
        features: [
          {
            label: 'Ajustar Estoque Manualmente no Hub',
            value: 'stocks:adjust-manually'
          },

        ]
      },
      {
        label: 'Serviços',
        value: 'menu:productsServices',
        type: 'menu',
        features: []
      },
      {
        label: 'Pedidos de Reposição',
        value: 'menu:replacementOrders',
        type: 'menu',
        features: []
      },
      {
        label: 'Produção',
        value: 'menu:production',
        type: 'menu',
        features: []
      },
      {
        label: 'Vendedores',
        value: 'menu:sellers',
        type: 'menu',
        features: []
      },
      {
        label: 'Acessa HCP',
        value: 'menu:hcp',
        type: 'menu',
        features: [
           { label: 'HCP | Fluxo 1: Arte Final', value: 'feature:hcp-arte' },
           { label: 'HCP | Fluxo 2: Montagem de Cartelas', value: 'feature:hcp-montagem' },
           { label: 'HCP | Configurações (Prazos e Fluxos)', value: 'feature:hcp-config' },
           { label: 'HCP | Gestão de Equipe HCP', value: 'feature:hcp-colab' },
        ]
      }
    ];

    if (onlyHCP) {
      // No HCP: apenas permissões HCP. Admin Global é gerenciado exclusivamente pelo HUB.
      return allOptions.filter(opt =>
        opt.value === 'menu:hcp' ||
        opt.value === 'all:hcp'
      );
    }
    // No HUB Geral, mostramos tudo exceto os fluxos específicos do HCP (geridos no outro portal)
    return allOptions.filter(opt => 
      !opt.value.startsWith('menu:hcp') && 
      !opt.value.startsWith('feature:hcp-') &&
      opt.value !== 'all:hcp'
    );
  }, [onlyHCP]);

  const setHCPDefaults = () => {
    setValue('swtich-menu:hcp', true);
    setValue('swtich-feature:hcp-arte', true);
    setValue('swtich-feature:hcp-montagem', true);
    setValue('swtich-feature:hcp-config', true);
    setValue('swtich-feature:hcp-colab', true);
  };

  // Estado para controlar quais menus estão expandidos
  const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>({})

  const toggleMenuExpansion = (menuValue: string) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuValue]: !prev[menuValue]
    }))
  }


  const loadCollaborator = async (collaboratorId: string) => {

    try {
      setLoading(true)

      const showResult = await showCollaboratorsUseCase.handle(collaboratorId);

      if (showResult.isFailure) {
        showToast(String(showResult?.error?.error), 'error');
        return;
      }

      const result = showResult.getValue()

      if (result) {
        setData(result)

        setValue('name', result.name)
        setValue('username', result.username)

        result?.permissionsScreens?.forEach((permission: string) => {
          if (permission === 'all' || permission === 'all:hub' || permission === 'all:hcp') {
            setValue(`swtich-${permission}`, true);
            return;
          }
           
          const parts = permission.split(':')
          const getPermissionKey = parts.slice(0, -1).join(':') // Remove a última parte (:manage)
          if (getPermissionKey) {
            setValue(`swtich-${getPermissionKey}`, true)
          }
        })

      }

    } catch (error) {
      console.log("error loadCollaborator function: ", error)
    } finally {
      setLoading(false);
    }
  };



  useEffect(() => {
    if (id) {
      loadCollaborator(id)
    }
  }, [id])




  return {
    loading,
    handleAction,
    register,
    control,
    formState,
    watch,
    handleSubmit,
    errors,
    menuOptions,
    expandedMenus,
    toggleMenuExpansion,
    setValue,
    reset,
    setHCPDefaults,
    loadCollaborator,
    data
  }
}